/**
 * Utility functions for calculating scheduled dates based on event types and contact data.
 * These functions are used by ScheduleCardModal to determine when cards should be sent.
 *
 * IMPORTANT: Date handling in this application
 * ---------------------------------------------
 * Dates like birthdays and anniversaries represent calendar dates (e.g., "May 15th"),
 * NOT specific moments in time. When working with these dates:
 *
 * 1. **Parsing**: Always use `parseAsLocalDate()` to parse ISO strings from the API.
 *    This prevents dates from shifting due to UTC-to-local conversion.
 *    Example: '2025-02-08T00:00:00Z' should give Feb 8, not Feb 7 in Mountain Time.
 *
 * 2. **Saving**: Use `toISOFromDateString()` for calendar dates from SmartDateInput (Mantine v8)
 *    (values are `"YYYY-MM-DD"` strings). Use `toLocalISOString()` only when you already have
 *    a `Date` at local midnight (e.g. legacy code paths). Never use `new Date("YYYY-MM-DD")`
 *    before formatting — that parses as UTC midnight and shifts the calendar day in many zones.
 *
 * 3. **Displaying**: Use `parseAsLocalDate()` before formatting for display.
 */

import dayjs from 'dayjs';
import { DateSourceType, FloatingHolidayRule } from '../types';
import type { Contact, ContactCustomDate, EventType } from '../types';

/**
 * Quick-select preset compatible with SmartDateInput `presets`.
 */
export interface DatePresetValue {
  value: string;
  label: string;
}

/**
 * Standard date format used throughout the application for user input.
 * MM/DD/YYYY format (e.g., "02/14/2026" for February 14, 2026).
 */
export const DATE_INPUT_FORMAT = 'MM/DD/YYYY';

/**
 * Placeholder text for date inputs.
 */
export const DATE_INPUT_PLACEHOLDER = 'MM/DD/YYYY';

/**
 * Pads a number to two digits for YYYY-MM-DD components.
 */
const padTwoDigits = (n: number): string => String(n).padStart(2, '0');


/**
 * Extracts the `"YYYY-MM-DD"` calendar portion from an API ISO string for SmartDateInput (Mantine v8).
 * Use this when binding date input `value` so the control stays string-based (avoids Date/string mismatch).
 *
 * @param isoString - ISO string from the API (e.g. `"1976-03-20T00:00:00Z"`) or null/undefined
 * @returns `"YYYY-MM-DD"` or null if missing/invalid
 */
export const toDateString = (isoString: string | null | undefined): string | null => {
  if (isoString == null || !String(isoString).trim()) {
    return null;
  }

  const datePart = String(isoString).trim().split('T')[0];

  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }

  return null;
};

/**
 * Converts a Mantine v8 date-only string (or leading `"YYYY-MM-DD"` of an ISO string) to the
 * wire format sent to the API. Does not use `new Date("YYYY-MM-DD")`.
 *
 * @param dateStr - `"YYYY-MM-DD"` (or ISO string; only the date part is used)
 * @returns e.g. `"1976-03-20T00:00:00.000Z"`
 * @throws Error if no valid `YYYY-MM-DD` prefix exists
 */
export const toISOFromDateString = (dateStr: string): string => {
  const datePart = dateStr.trim().split('T')[0];

  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    throw new Error(`Invalid calendar date string: ${dateStr}`);
  }

  return `${datePart}T00:00:00.000Z`;
};

/**
 * Today's date in local time as `"YYYY-MM-DD"` for SmartDateInput `maxDate` (Mantine v8 string API).
 */
export const todayDateString = (): string => {
  const now = new Date();

  return `${now.getFullYear()}-${padTwoDigits(now.getMonth() + 1)}-${padTwoDigits(now.getDate())}`;
};

/**
 * Today's calendar date in UTC as `YYYY-MM-DD` (scheduling bounds, Florist One policy).
 */
export const utcTodayDateString = (): string => {
  const now = new Date();

  return `${now.getUTCFullYear()}-${padTwoDigits(now.getUTCMonth() + 1)}-${padTwoDigits(now.getUTCDate())}`;
};

/** `YYYY-MM-DD` from a {@link Date}'s UTC calendar components (scheduling YMD storage). */
export const utcDateToYmdString = (date: Date): string =>
  `${date.getUTCFullYear()}-${padTwoDigits(date.getUTCMonth() + 1)}-${padTwoDigits(date.getUTCDate())}`;

/**
 * Stable `YYYY-MM-DD` for scheduling state from a calendar {@link Date}.
 * Encodes via {@link toLocalISOString} (UTC wire) then reads YMD with {@link utcDateToYmdString}
 * so the result does not depend on the machine time zone.
 */
export const schedulingYmdFromDate = (date: Date): string => {
  const wireIso = toLocalISOString(date);
  const ymd = utcDateToYmdString(new Date(wireIso));
  return ymd;
};

/**
 * Shifts a date to Monday when it falls on Sunday (flower delivery constraint).
 */
export const avoidSundayForDelivery = (date: Date): Date => {
  if (date.getDay() === 0) {
    const shifted = new Date(date);
    shifted.setDate(shifted.getDate() + 1);
    return shifted;
  }

  return date;
};

/**
 * Tomorrow as YYYY-MM-DD for SmartDateInput presets and minDate helpers.
 */
export const tomorrowDateString = (options?: { avoidSunday?: boolean }): string => {
  let date = dayjs().add(1, 'day').toDate();

  if (options?.avoidSunday) {
    date = avoidSundayForDelivery(date);
  }

  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * One week from today as YYYY-MM-DD for SmartDateInput presets.
 */
export const nextWeekDateString = (options?: { avoidSunday?: boolean }): string => {
  let date = dayjs().add(1, 'week').toDate();

  if (options?.avoidSunday) {
    date = avoidSundayForDelivery(date);
  }

  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Shared Tomorrow / Next week quick-select presets for future delivery or arrival dates.
 */
export const buildFutureDatePresets = (options?: { avoidSunday?: boolean }): DatePresetValue[] => [
  { value: tomorrowDateString(options), label: 'Tomorrow' },
  { value: nextWeekDateString(options), label: 'Next week' },
];

/**
 * Checks if an event type is a fixed-date holiday (e.g., Valentine's Day on Feb 14).
 * Fixed-date events occur on the same calendar date each year and don't require
 * a contact-specific date field.
 *
 * @param eventType - The event type to check
 * @returns True if this is a fixed-date event with both month and day specified
 *
 * @example
 * // Valentine's Day has fixedMonth=2, fixedDay=14
 * isFixedDateEventType(valentinesDay); // returns true
 *
 * // Birthday has fixedMonth=null, fixedDay=null
 * isFixedDateEventType(birthday); // returns false
 */
export const isFixedDateEventType = (eventType: EventType): boolean => {
  // Check explicit discriminator first, fall back to legacy field check
  if (eventType.dateSourceType === DateSourceType.FixedDate) {
    return true;
  }

  // Legacy check for backward compatibility with data created before DateSourceType was added
  return eventType.fixedMonth != null && eventType.fixedDay != null;
};

/**
 * Checks if an event type is a floating holiday (date changes each year by rule).
 */
export const isFloatingHolidayEventType = (eventType: EventType): boolean => {
  return eventType.dateSourceType === DateSourceType.FloatingHoliday;
};

/**
 * Parses an ISO date string as a local date, ignoring timezone.
 * This prevents dates from shifting due to UTC conversion.
 *
 * For example, '1990-05-15T00:00:00Z' should give us May 15,
 * not May 14 (which would happen if UTC midnight is converted to local time).
 *
 * @param isoString - ISO date string (e.g., '1990-05-15T00:00:00Z')
 * @returns Date object with the date interpreted as local time
 *
 * @example
 * // In Mountain Time (UTC-7), this would incorrectly give May 14 with new Date()
 * parseAsLocalDate('1990-05-15T00:00:00Z'); // Correctly gives May 15
 */
export const parseAsLocalDate = (isoString: string): Date => {
  // Extract the date part (YYYY-MM-DD) and create a local date
  // This avoids timezone conversion issues
  const datePart = isoString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);

  // Month is 0-indexed in JavaScript Date
  return new Date(year, month - 1, day);
};

/**
 * Converts a Date object to an ISO string preserving the local date.
 * This is the inverse of parseAsLocalDate.
 *
 * When the user selects "February 8" in a date picker, we want to save
 * "February 8" regardless of their timezone. This function formats the
 * local date as an ISO string with midnight UTC.
 *
 * @param date - A Date object representing a local date
 * @returns ISO string in format 'YYYY-MM-DDTHH:mm:ss.sssZ'
 *
 * @example
 * // User in Mountain Time selects Feb 8
 * const localDate = new Date(2025, 1, 8); // Feb 8, 2025 local
 * toLocalISOString(localDate); // '2025-02-08T00:00:00.000Z'
 */
export const toLocalISOString = (date: Date): string => {
  // Extract the local year, month, day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // Format as ISO string with midnight UTC
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

/**
 * Formats a date string for display in a human-readable format.
 * Uses parseAsLocalDate to correctly handle timezone.
 *
 * @param dateString - ISO date string from the API
 * @param options - Intl.DateTimeFormat options (defaults to short month, day, and year)
 * @returns Formatted date string (e.g., "Feb 8, 2026")
 */
export const formatDateForDisplay = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
): string => {
  const date = parseAsLocalDate(dateString);
  return date.toLocaleDateString(undefined, options);
};

/**
 * Formats a date string for display with short date format (Month Day, 2-digit Year).
 * Convenience wrapper around formatDateForDisplay.
 * Returns null if dateString is null/undefined.
 *
 * @param dateString - ISO date string from the API (or null/undefined)
 * @returns Formatted date string (e.g., "Feb 8, 2026") or null
 */
export const formatShortDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    return formatDateForDisplay(dateString, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

/**
 * Compact month + day for tight overview rows (omits year).
 */
export const formatOverviewRowDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    return formatDateForDisplay(dateString, { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

/**
 * Formats a calendar date for display, optionally omitting the year when it is not meaningful
 * (e.g. birthday or anniversary stored with a sentinel year — see API {@code DateConstants.UnknownYear}).
 */
export const formatPartialDate = (
  dateString: string | null | undefined,
  yearKnown: boolean = true
): string | null => {
  if (!dateString) return null;
  try {
    const options: Intl.DateTimeFormatOptions = yearKnown
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' };
    return formatDateForDisplay(dateString, options);
  } catch {
    return null;
  }
};

/**
 * Formats a contact's birthday for display, respecting `birthdayYearKnown` when present.
 */
export const formatContactBirthday = (contact: {
  birthday?: string;
  birthdayYearKnown?: boolean;
}): string | null => {
  if (!contact.birthday) return null;
  const yearKnown = contact.birthdayYearKnown !== false;
  return formatPartialDate(contact.birthday, yearKnown);
};

/**
 * Formats a custom anniversary date for display, respecting `anniversaryStartYearKnown` when present.
 */
export const formatCustomDate = (customDate: {
  anniversaryStartDate: string;
  anniversaryStartYearKnown?: boolean;
}): string | null => {
  if (!customDate.anniversaryStartDate) return null;
  const yearKnown = customDate.anniversaryStartYearKnown !== false;
  return formatPartialDate(customDate.anniversaryStartDate, yearKnown);
};

/**
 * Formats a date string for display with year (Month Day, Year format).
 * Useful for anniversary dates where the start year is meaningful.
 * Returns null if dateString is null/undefined.
 *
 * @param dateString - ISO date string from the API (or null/undefined)
 * @returns Formatted date string (e.g., "Feb 8, 2025") or null
 */
export const formatDateWithYear = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    return formatDateForDisplay(dateString, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

/**
 * Formats a fixed month and day for display (e.g., Valentine's Day on Feb 14).
 * This is for fixed-date events that have numeric month/day values.
 *
 * @param month - Month number (1-12, NOT 0-indexed)
 * @param day - Day of month (1-31)
 * @returns Formatted date string (e.g., "Feb 14")
 */
export const formatFixedDateForDisplay = (month: number, day: number): string => {
  // Create a date object to format the month name.
  // Year 2000 matches API {@code DateConstants.UnknownYear} (leap year for Feb 29) but is only a
  // formatting anchor here — the formatted output never includes the year.
  const date = new Date(2000, month - 1, day);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Gets the month name for a month number.
 *
 * @param month - Month number (1-12, NOT 0-indexed)
 * @returns Short month name (e.g., "Feb")
 */
export const getMonthName = (month: number): string => {
  const date = new Date(2000, month - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'short' });
};

/**
 * Gets the event date for a contact based on the event type.
 *
 * @param contact - The contact whose date to retrieve
 * @param eventType - The event type determining which date field to use
 * @returns The event date as a Date object, or null if not found
 *
 * @example
 * // For Birthday event type, returns contact.birthday
 * getEventDateForContact(contact, birthdayEventType);
 *
 * // For custom "Wedding Anniversary" event type, looks up matching custom date
 * getEventDateForContact(contact, weddingAnniversaryEventType);
 */
export const getEventDateForContact = (contact: Contact, eventType: EventType): Date | null => {
  // Fixed-date and floating holiday events don't use contact dates
  if (isFixedDateEventType(eventType) || isFloatingHolidayEventType(eventType)) {
    return null;
  }

  // Birthday is a predefined date field on the contact
  if (eventType.dateFieldName === 'Birthday') {
    // Parse as local date to avoid timezone-related day shifting
    return contact.birthday ? parseAsLocalDate(contact.birthday) : null;
  }

  // Look for a matching custom date by name
  const customDate = contact.customDates?.find(
    (cd: ContactCustomDate) => cd.name === eventType.dateFieldName
  );

  // Parse as local date to avoid timezone-related day shifting
  return customDate?.anniversaryStartDate ? parseAsLocalDate(customDate.anniversaryStartDate) : null;
};

/**
 * Calculates the next occurrence for a fixed-date event (e.g., Valentine's Day on Feb 14).
 * If this year's date has passed, returns next year's date.
 *
 * @param fixedMonth - The month (1-12, NOT 0-indexed)
 * @param fixedDay - The day of month (1-31)
 * @returns The next occurrence of that fixed date
 *
 * @example
 * // If today is January 15, 2026
 * getNextFixedDateOccurrence(2, 14); // Returns Feb 14, 2026
 *
 * // If today is March 1, 2026
 * getNextFixedDateOccurrence(2, 14); // Returns Feb 14, 2027
 */
export const getNextFixedDateOccurrence = (fixedMonth: number, fixedDay: number): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const thisYear = today.getFullYear();

  // Create this year's occurrence
  // Note: JavaScript Date month is 0-indexed, so subtract 1 from the 1-indexed fixedMonth
  const thisYearOccurrence = new Date(thisYear, fixedMonth - 1, fixedDay);

  // If this year's occurrence is in the future (or today), use it
  if (thisYearOccurrence >= today) {
    return thisYearOccurrence;
  }

  // Otherwise, use next year's occurrence
  return new Date(thisYear + 1, fixedMonth - 1, fixedDay);
};

/**
 * Calculates the next occurrence of an annual event date (e.g., birthday, anniversary).
 * If this year's date has passed, returns next year's date.
 *
 * @param eventDate - The original event date (e.g., birth date from 1990)
 * @returns The next occurrence of that date in the current or next year
 *
 * @example
 * // Birthday is May 15, 1990. If today is March 1, 2026:
 * getNextEventOccurrence(new Date('1990-05-15')); // Returns May 15, 2026
 *
 * // If today is June 1, 2026:
 * getNextEventOccurrence(new Date('1990-05-15')); // Returns May 15, 2027
 */
export const getNextEventOccurrence = (eventDate: Date): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const thisYear = today.getFullYear();

  // Create this year's occurrence using the month and day from the original event date
  const thisYearOccurrence = new Date(thisYear, eventDate.getMonth(), eventDate.getDate());

  // If this year's occurrence is in the future (or today), use it
  if (thisYearOccurrence >= today) {
    return thisYearOccurrence;
  }

  // Otherwise, use next year's occurrence
  return new Date(thisYear + 1, eventDate.getMonth(), eventDate.getDate());
};

// ---------------------------------------------------------------------------
// Floating holiday calculation algorithms
// These mirror the C# implementations in EventTypeService for frontend parity.
//
// Shared test vectors (must match C# tests):
// - Easter 2025: April 20    - Easter 2026: April 5
// - Easter 2027: March 28    - Easter 2028: April 16
// - Mother's Day (US) 2026: May 10    - Mother's Day (US) 2027: May 9
// - Thanksgiving 2026: November 26
// ---------------------------------------------------------------------------

/**
 * Finds the Nth occurrence of a specific weekday in a given month.
 * @param year - The year
 * @param month - The month (1-12, NOT 0-indexed)
 * @param weekday - Day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @param ordinal - Which occurrence (1=first, 2=second, etc.)
 */
export const calculateNthWeekdayOfMonth = (
  year: number,
  month: number,
  weekday: number,
  ordinal: number
): Date => {
  // JS Date month is 0-indexed
  const firstOfMonth = new Date(year, month - 1, 1);
  const daysUntilWeekday = (weekday - firstOfMonth.getDay() + 7) % 7;
  const day = 1 + daysUntilWeekday + (ordinal - 1) * 7;
  return new Date(year, month - 1, day);
};

/**
 * Finds the last occurrence of a specific weekday in a given month.
 * @param year - The year
 * @param month - The month (1-12, NOT 0-indexed)
 * @param weekday - Day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
 */
export const calculateLastWeekdayOfMonth = (
  year: number,
  month: number,
  weekday: number
): Date => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const lastOfMonth = new Date(year, month - 1, daysInMonth);
  const daysBack = (lastOfMonth.getDay() - weekday + 7) % 7;
  return new Date(year, month - 1, daysInMonth - daysBack);
};

/**
 * Calculates Easter Sunday for a given year using the Anonymous Gregorian Computus algorithm.
 * Accurate for all years in the Gregorian calendar (1583-present).
 */
export const calculateEasterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  // month is 1-indexed from algorithm, JS Date expects 0-indexed
  return new Date(year, month - 1, day);
};

/**
 * Calculates the floating holiday date for a specific year based on event type rule fields.
 */
export const calculateFloatingHolidayForYear = (eventType: EventType, year: number): Date | null => {
  if (!eventType.floatingHolidayRule) {
    return null;
  }

  switch (eventType.floatingHolidayRule) {
    case FloatingHolidayRule.NthWeekdayOfMonth:
      if (
        eventType.floatingRuleMonth != null &&
        eventType.floatingRuleWeekday != null &&
        eventType.floatingRuleOrdinal != null
      ) {
        return calculateNthWeekdayOfMonth(
          year,
          eventType.floatingRuleMonth,
          eventType.floatingRuleWeekday,
          eventType.floatingRuleOrdinal
        );
      }
      return null;

    case FloatingHolidayRule.LastWeekdayOfMonth:
      if (eventType.floatingRuleMonth != null && eventType.floatingRuleWeekday != null) {
        return calculateLastWeekdayOfMonth(
          year,
          eventType.floatingRuleMonth,
          eventType.floatingRuleWeekday
        );
      }
      return null;

    case FloatingHolidayRule.EasterOffset: {
      const easter = calculateEasterSunday(year);
      const offset = eventType.floatingRuleEasterOffset ?? 0;
      const result = new Date(easter);
      result.setDate(result.getDate() + offset);
      return result;
    }

    default:
      return null;
  }
};

/**
 * Calculates the next occurrence for a floating holiday event type.
 * If this year's date has passed, returns next year's date.
 */
export const getNextFloatingHolidayOccurrence = (eventType: EventType): Date | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thisYear = today.getFullYear();

  const thisYearDate = calculateFloatingHolidayForYear(eventType, thisYear);
  if (!thisYearDate) {
    return null;
  }

  if (thisYearDate >= today) {
    return thisYearDate;
  }

  return calculateFloatingHolidayForYear(eventType, thisYear + 1);
};

/**
 * Weekday names for display.
 */
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDINAL_NAMES = ['1st', '2nd', '3rd', '4th', '5th'];

/**
 * Returns a human-readable description of the floating holiday rule.
 * Examples: "2nd Sunday of May", "Last Monday of May", "Easter Sunday"
 */
export const formatFloatingHolidayForDisplay = (eventType: EventType): string => {
  if (!eventType.floatingHolidayRule) {
    return '';
  }

  switch (eventType.floatingHolidayRule) {
    case FloatingHolidayRule.NthWeekdayOfMonth:
      if (
        eventType.floatingRuleMonth != null &&
        eventType.floatingRuleWeekday != null &&
        eventType.floatingRuleOrdinal != null
      ) {
        const ordinal = ORDINAL_NAMES[eventType.floatingRuleOrdinal - 1] || `${eventType.floatingRuleOrdinal}th`;
        const weekday = WEEKDAY_SHORT[eventType.floatingRuleWeekday] || '';
        const monthName = getMonthName(eventType.floatingRuleMonth);
        return `${ordinal} ${weekday} of ${monthName}`;
      }
      return '';

    case FloatingHolidayRule.LastWeekdayOfMonth:
      if (eventType.floatingRuleMonth != null && eventType.floatingRuleWeekday != null) {
        const weekday = WEEKDAY_NAMES[eventType.floatingRuleWeekday] || '';
        const monthName = getMonthName(eventType.floatingRuleMonth);
        return `Last ${weekday} of ${monthName}`;
      }
      return '';

    case FloatingHolidayRule.EasterOffset: {
      const offset = eventType.floatingRuleEasterOffset ?? 0;
      if (offset === 0) return 'Easter Sunday';
      if (offset === -2) return 'Good Friday';
      if (offset === 1) return 'Easter Monday';
      if (offset > 0) return `Easter +${offset} days`;
      return `Easter ${offset} days`;
    }

    default:
      return '';
  }
};
